/**
 * GuildDetailBody.jsx — pure renderer for the `guild-detail` widget data.
 *
 * Issue #92 — used by GuildDetailModal (popup) and the GuildDetail
 * widget renderer (open-in-window promotion). No data fetching here;
 * the caller passes `{ data, loading, error, name }` and we render only.
 */
import { Show, For } from 'solid-js';
import { gold, qty } from '../lib/format.js';
import { pickUser } from '../lib/picks.js';
import wm from '../lib/wmStore.js';
import MissingGuildPlaceholder from '../components/MissingGuildPlaceholder.jsx';

export default function GuildDetailBody(props) {
  const data = () => props.data;
  const weekOffset = () => Number(props.weekOffset ?? data()?.weekOffset ?? 0);
  const tradeWeekLabel = () => weekOffset() === 0 ? 'Current trade week' : `${weekOffset()} weeks ago`;
  return (
    <Show when={!props.loading} fallback={<div class="v2-widget-skel">Loading…</div>}>
      <Show when={!props.error} fallback={<div class="v2-error">{props.error}</div>}>
        <Show when={data()?.found && data()?.guild} fallback={
          <div class="v2-empty" style={{ display: 'flex', 'flex-direction': 'column', gap: '6px', 'align-items': 'flex-start' }}>
            <span>Guild “{props.name || 'unknown'}” isn't tracked.</span>
            <Show when={props.name}>
              <MissingGuildPlaceholder guildSlug={props.name} label="What we know" />
            </Show>
          </div>
        }>
          {(() => {
            const d = data();
            const cw = d.currentWeek || {};
            const lt = d.lifetime || {};
            return (
              <>
                <div class="v2-stat-grid">
                  <div class="v2-stat v2-stat--gold">
                    <span class="v2-stat__label" title={tradeWeekLabel()}>Gold (T-WK)</span>
                    <span class="v2-stat__value">{gold(cw.totalGold)}</span>
                    <span class="v2-stat__sub">{qty(cw.sellerCount)} sellers</span>
                  </div>
                  <div class="v2-stat v2-stat--cyan">
                    <span class="v2-stat__label">Sales</span>
                    <span class="v2-stat__value">{qty(cw.totalSales)}</span>
                  </div>
                  <div class="v2-stat">
                    <span class="v2-stat__label">Items</span>
                    <span class="v2-stat__value">{qty(cw.totalItems)}</span>
                  </div>
                </div>
                <Show when={lt.totalSales > 0}>
                  <div class="v2-rank-row__sub" style={{ 'margin-top': '6px' }}>
                    Lifetime: {qty(lt.totalSales)} sales · {gold(lt.totalGold)} · {qty(lt.totalItems)} items
                  </div>
                </Show>
                <Show when={wm.state.identity?.isAdmin && d.ttcIntel?.snapshot}>
                  {(() => {
                    const snap = d.ttcIntel.snapshot;
                    return (
                      <div style={{ 'margin-top': '10px', padding: '8px', border: '1px solid var(--window-edge, rgba(74,222,128,0.16))', 'border-radius': '6px', background: 'rgba(0,0,0,0.18)' }}>
                        <div class="v2-section-h" style={{ 'margin-top': 0 }}>
                          🧭 TTC Kiosk Shelf {snap.kioskCity ? `· ${snap.kioskCity} (${snap.kioskZone})` : ''}
                        </div>
                        <div class="v2-stat-grid" style={{ 'margin-top': '4px' }}>
                          <div class="v2-stat v2-stat--gold">
                            <span class="v2-stat__label">Active Shelf</span>
                            <span class="v2-stat__value">{gold(snap.totalListedGold)}</span>
                            <span class="v2-stat__sub">{qty(snap.activeListings)} listings · {qty(snap.uniqueSellers)} sellers</span>
                          </div>
                          <div class="v2-stat v2-stat--cyan">
                            <span class="v2-stat__label">Sell Conversion</span>
                            <span class="v2-stat__value">{snap.sellThroughPct}%</span>
                            <span class="v2-stat__sub">{qty((snap.counts?.SOLD_CONFIRMED || 0) + (snap.counts?.SOLD_IMPLIED || 0))} sold</span>
                          </div>
                          <div class="v2-stat">
                            <span class="v2-stat__label">Cancel / Expire</span>
                            <span class="v2-stat__value">{snap.cancelPct}% / {snap.expiryPct}%</span>
                            <span class="v2-stat__sub">{qty(snap.counts?.CANCELLED_RELIST || 0)} undercut · {qty(snap.counts?.EXPIRED || 0)} exp</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </Show>
                <Show when={d.topSellers?.length}>
                  <div class="v2-section-h" title={tradeWeekLabel()}>🏆 Top sellers (T-WK)</div>
                  <ol class="v2-rank-list">
                    <For each={d.topSellers}>{(s, i) => (
                      <li class="v2-rank-row">
                        <span class="v2-rank-row__medal">{i() + 1}</span>
                        <span style={{ 'min-width': 0 }}>
                          <span class="v2-rank-row__name v2-pick v2-pick--user"
                            onClick={() => pickUser(s.sellerName)}>{s.sellerName}</span>
                          <span class="v2-rank-row__sub">{qty(s.totalSales)} sales</span>
                        </span>
                        <span class="v2-rank-row__value">{gold(s.totalGold)}</span>
                      </li>
                    )}</For>
                  </ol>
                </Show>
              </>
            );
          })()}
        </Show>
      </Show>
    </Show>
  );
}
